import type React from "react";
import { Box, Button } from "@salestrak/ui";

import { EditableField } from "#app/components/settings/editable-field";
import { ProfileAvatar } from "#app/components/settings/profile-avatar";
import { SettingsGroup, SettingsGroupRow } from "#app/components/settings/settings-group";
import { validatePhoneNumber } from "#app/lib/custom-field-utils";
import type { ProfileFormUser } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

const validatePhone = (value: string) => validatePhoneNumber(value, "US");

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// The prop type is the `ProfileForm_user` fragment (graphql/members.graphql) —
// this form declares the fields it renders and both screens that mount it
// spread them (R1). `email` is nullable on User, and the form renders it so.
export type { ProfileFormUser };

export type DangerZoneConfig = Omittable<{
  id: string;
  label: string;
  buttonLabel: string;
  variant: "destructive" | "default";
  onClick?: () => void;
  disabled?: boolean;
}>;

type ProfileFormProps = Omittable<{
  user: ProfileFormUser;
  onFieldSave: (fieldId: string, value: string) => void;
  dangerZone?: DangerZoneConfig[];
  /**
   * Extra settings groups rendered between General and the danger zone.
   * The self profile passes its Security group here; the admin member-edit
   * screen (which reuses this form for OTHER users) passes nothing.
   */
  children?: React.ReactNode;
}>;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProfileForm({ user, onFieldSave, dangerZone, children }: ProfileFormProps) {
  return (
    <>
      <SettingsGroup label="General">
        <SettingsGroupRow label="Avatar" subtitle="Recommended 256x256">
          <Box direction="row" justify="end">
            <ProfileAvatar user={user} />
          </Box>
        </SettingsGroupRow>

        <SettingsGroupRow label="Name">
          <EditableField
            fieldId="name"
            label="Name"
            initialValue={user.name ?? ""}
            onSave={onFieldSave}
          />
        </SettingsGroupRow>

        <SettingsGroupRow
          label="Username"
          subtitle="How you want to be called in SalesTrak"
        >
          <EditableField
            fieldId="username"
            label="Username"
            initialValue={user.username ?? ""}
            onSave={onFieldSave}
          />
        </SettingsGroupRow>

        <SettingsGroupRow label="Email">
          <EditableField
            fieldId="email"
            label="Email"
            initialValue={user.email ?? ""}
            disabled
            onSave={onFieldSave}
          />
        </SettingsGroupRow>

        <SettingsGroupRow label="Phone">
          <EditableField
            fieldId="phoneNumber"
            label="Phone"
            initialValue={user.phoneNumber ?? ""}
            onSave={onFieldSave}
            inputType="tel"
            validate={validatePhone}
            errorMessage="Invalid phone number"
          />
        </SettingsGroupRow>

        <SettingsGroupRow label="Title">
          <EditableField
            fieldId="title"
            label="Title"
            initialValue={user.title ?? ""}
            onSave={onFieldSave}
          />
        </SettingsGroupRow>
      </SettingsGroup>

      {children}

      {dangerZone && dangerZone.length > 0 ? (
        <SettingsGroup label="Danger zone">
          {dangerZone.map((action) => (
            <SettingsGroupRow key={action.id} label={action.label}>
              <Box direction="row" justify="end">
                <Button
                  disabled={action.disabled ?? !action.onClick}
                  onClick={action.onClick}
                  variant={action.variant === "destructive" ? "destructive-outline" : "ghost"}
                >
                  {action.buttonLabel}
                </Button>
              </Box>
            </SettingsGroupRow>
          ))}
        </SettingsGroup>
      ) : null}
    </>
  );
}
